<template>
  <div v-if="isLoaded">
    <div class="b-page__content-block">
      <h1>{{$t('CONTENT.contract.card_data_creation')}}</h1>
    </div>
    <div class="row">
      <server-error class="col-12" :error="error" />
    </div>
    <div class="px-6 pt-6">
      <form class="b-form p-0 pb-8" @submit.prevent="onSubmit" @keydown.enter.prevent="onKeyupEvent">
        <CardData :formData="formData" :validationErrors="validationErrors" :delete-lessor-disabled="currentStep >= 2"/>
        <div v-if="currentStep >= 2">
          <Assets :validationErrors="validationAssetsErrors"/>
        </div>
        <div v-if="currentStep >= 3">
          <Payments :contract="formData.contract" :validationErrors="validationPaymentErrors"/>
        </div>
        <div v-if="currentStep >= 4">
          <PaymentsCharts :errors="paymentChartsError" @createContractPreview="onCreateContractPreview" />
        </div>
        <div class="row mt-4">
          <div class="col-6">
            <div class="b-form__container b-form__container_type_flex">
              <button
                class="b-btn b-btn_type_primary b-btn_type_save"
                type="button"
                :disabled="paymentChartsErrors"
                @click="changeCurrentStep"
                data-cy="next-step"
              >{{currentStep === 3 || !isHideCharts ? $t('CONTENT.contract.calculate') : currentStep >= 4 ? $t('CONTENT.save') : $t('CONTENT.next')}}</button>
              <button
                class="b-btn b-btn_type_empty b-btn_type_save"
                data-cy="set-next-step"
                @click="$router.push({ name: 'Contracts' })"
              >
                {{ $t("CONTENT.cancel") }}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import CardData from "../components/registry/contracts/create/CardData";
import Assets from "../components/registry/contracts/create/assets/GroupedAssetComponentsByAssets";
import Payments from "../components/registry/contracts/create/payments/Payments";
import ContractAggregate from "../models/contract/ContractAggregate";
import PaymentsCharts from "../components/registry/contracts/create/payments/PaymentsCharts";
import {mapGetters} from "vuex";

export default {
  components: {
    CardData,
    Assets,
    Payments,
    PaymentsCharts
  },
  data() {
    return {
      formData: null,
      currentStep: 1,
      error: null,
      isHideCharts: true,
      validationErrors: [],
      validationAssetsErrors: [],
      validationPaymentErrors: [],
      paymentChartsErrors: null
    };
  },
  computed: {
    ...mapGetters(["getCurrentContract"]),
    assetComponents() {
      return this.$store.getters["assetComponentsPlugin/getAllAssetComponents"]
    }
  },
  watch: {
    'assetComponents': {
      deep: true,
      handler() {
        if(this.currentStep > 3) this.currentStep = 3
      }
    }
  },
  async created() {
    try {
      this.$loadingManager.addLoadingStep();
      await Promise.all([
        this.$store.dispatch("getAllCounterparties"),
        this.$store.dispatch("loadAllUnderlyingAssetTypes"),
        this.$store.dispatch('loadAllBusinessUnits'),
        this.$store.dispatch("loadAllAssets"),
        this.$store.dispatch("loadAllFrequencies"),
        this.$store.dispatch("loadLeaseFrequency"),
        this.$store.dispatch("getUnitOfMeasure"),
        this.$store.dispatch("loadAllPaymentTypes"),
        this.$store.dispatch("loadAllCurrencyCode"),
        this.$store.dispatch("loadAllPaymentName"),
        this.$store.dispatch("loadAllPaymentKindsAndTypes"),
        this.$store.dispatch("loadContractSettings"),
        this.$store.dispatch("getAllCounterpartyAddressTypes"),
        this.$store.dispatch("getAllDirectoryLegalForms"),
        this.$store.dispatch("getAllCounterpartyTypes"),
        this.$store.dispatch("indexationPlugin/loadAllIndexations")
      ]);
      this.formData = ContractAggregate.createNew();
      this.$store.commit("SET_CURRENT_CONTRACT", this.formData);
      this.$store.state.assetComponentsPlugin.assetComponents = [];
      this.$loadingManager.loadingStepLoaded();
    } catch (e) {
      this.$loadingManager.hideLoadScreen();
      this.error = e;
    }
  },
  methods: {
    paymentChartsError(errors){
      this.paymentChartsErrors = errors;
    },
    async onSubmit() {
      try {
        this.$loadingManager.addLoadingStep();
        await this.$store.dispatch("createContract", { assetComponents: this.assetComponents });
        this.$loadingManager.loadingStepLoaded();
        this.$router.push("/registry/contracts");
      } catch (e) {
        this.$loadingManager.hideLoadScreen();
        this.error = e;
      }
    },
    changeCurrentStep() {
      if (!this.validatesStep(this.currentStep)) return;
      this.currentStep = this.currentStep + 1;
      if (this.currentStep >= 4) {
        this.$store.commit("SET_VIEW_STATE_OF_CHARTS", false);
      }
      if (this.currentStep > 4) {
        this.onSubmit();
      }
    },
    onCreateContractPreview(previewData) {
      previewData.assetComponents
        .flatMap(assetComponent => assetComponent.lessorComponentShares)
        .flatMap(lessorComponentShare => lessorComponentShare.paymentsParameters)
        .forEach(paymentsParameter => {
          this.$store.commit("assetComponentsPlugin/updatePaymentDiscountByPaymentId", {
            id: paymentsParameter.idInDB,
            value: paymentsParameter.discount.discountRate
          })
        })
    },
    validatesStep(step) {
      let isValidStepOne = false;
      let isValidStepTwo = false;
      let isValidStepThree = false;
      this.error = null
      switch (step) {
        case 1:
          isValidStepOne = this.validateStepOne();
          return isValidStepOne;
        case 2:
          isValidStepOne = this.validateStepOne();
          isValidStepTwo = this.validateStepTwo();
          return isValidStepOne && isValidStepTwo;
        default:
          isValidStepOne = this.validateStepOne();
          isValidStepTwo = this.validateStepTwo();
          isValidStepThree = this.validateStepThree();
          return isValidStepOne && isValidStepTwo && isValidStepThree;
      }
    },
    validateStepOne() {
      this.validationErrors = [];
      const contract = this.$store.getters["getCurrentContract"].contract;
      if (!contract.number) {
        this.validationErrors.push("number");
      }
      if (!contract.date) {
        this.validationErrors.push("date");
      }
      if (!contract.createDate) {

      }
      if (!contract.lesseeCounterpartyId) {
        this.validationErrors.push("currentLessee");
      }
      if (!this.arrayIsNotEmpty(contract.lessorCounterpartyIds)) {
        this.validationErrors.push("lessor");
      }
      return this.validationErrors.length <= 0;
    },
    validateStepTwo() {
      this.validationAssetsErrors = [];
      if (!this.arrayIsNotEmpty(this.assetComponents)) {
        this.validationAssetsErrors[0] = [];
        this.validationAssetsErrors[0].push("newAsset");
      }

      for (const [index, asset] of this.assetComponents.entries()) {
        this.validationAssetsErrors[index] = [];
        if (!asset.quantity) {
          this.validationAssetsErrors[index].push("quantity");
        }
        if (!asset.unitOfMeasureCode) {
          this.validationAssetsErrors[index].push("unitOfMeasure");
        }
        if (!asset.endDate) {
          this.validationAssetsErrors[index].push("endDate");
        }
        if (!asset.startDate) {
          this.validationAssetsErrors[index].push("startDate");
        }
      }
      return !this.validationAssetsErrors.some(e => e.length > 0);
    },
    validateLeaseFrequency(assetIndex, lessorComponentShares) {
      const firstLeaseFrequency = lessorComponentShares[0].paymentsParameters.filter(pp => pp.leaseFrequency != 'Once')[0].leaseFrequency;
      return lessorComponentShares.every(share => share.paymentsParameters.every(pp => pp.leaseFrequency == 'Once' || pp.leaseFrequency === firstLeaseFrequency));
    },

    validateStepThree() {
      for (const [index, asset] of this.assetComponents.entries()) {
        this.$set(this.validationPaymentErrors, index, []);
        const allSameLeaseFrequency = this.validateLeaseFrequency(index, asset.lessorComponentShares);
        if(allSameLeaseFrequency === false)
        {
          this.error = this.$t('ERRORS.PaymentsHaveDifferentLeaseFrequency');
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })
          return false;
        }
        asset.lessorComponentShares.forEach(lessorComponentShare => this.validateLessor(index, lessorComponentShare))
      }

      const assetLessorsValid = this.validateAssetLessors()

      if(!assetLessorsValid) {
        this.error = this.$t('ERRORS.NotAllLessorsHaveAsset')
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
        return false
      }

      console.log(this.error);

      return !this.validationPaymentErrors.some(errors => errors.some(error => error.length > 0));
    },
    validateAssetLessors() {
      const contract = this.$store.getters["getCurrentContract"].contract
      return contract.lessorCounterpartyIds.every(lessorCounterpartyId => {
        return this.assetComponents
          .flatMap(assetComponent => assetComponent.lessorComponentShares)
          .some(lessorComponentShare => lessorComponentShare.lessorId === lessorCounterpartyId)
      })
    },
    validateLessor(assetIndex, lessorComponentShare) {
      for (const [paymentIndex, payment] of lessorComponentShare.paymentsParameters.entries()) {
        this.$set(this.validationPaymentErrors[assetIndex], paymentIndex, []);
        if (!payment.paymentNameId) {
          this.validationPaymentErrors[assetIndex][paymentIndex].push("currentPayment");
        }
        if (!payment.currencyCode) {
          this.validationPaymentErrors[assetIndex][paymentIndex].push("currentCurrencyCode");
        }
        if (!payment.leaseFrequency) {
          this.validationPaymentErrors[assetIndex][paymentIndex].push("paymentLeaseFrequency");
        }
        if (!payment.startDate) {
          this.validationPaymentErrors[assetIndex][paymentIndex].push("startDate");
        }
        if (!payment.endDate) {
          this.validationPaymentErrors[assetIndex][paymentIndex].push("endDate");
        }
        if (!payment.firstPaymentDate) {
          this.validationPaymentErrors[assetIndex][paymentIndex].push("firstPaymentDate");
        }
        if (!payment.paymentFrequency) {
          this.validationPaymentErrors[assetIndex][paymentIndex].push("paymentPaymentFrequency");
        }
        if (!payment.indexation || !payment.indexation.indexationKindId) {
          this.validationPaymentErrors[assetIndex][paymentIndex].push("indexationKindId");
        }
        if (!payment.discount || (!payment.discount.isAuto && (payment.discount.discountRate === null || payment.discount.discountRate === undefined))) {
          this.validationPaymentErrors[assetIndex][paymentIndex].push("discountPercent");
        }
        if (payment.leaseRate === null || payment.leaseRate === undefined) {
          this.validationPaymentErrors[assetIndex][paymentIndex].push("leaseRate");
        }
        if (!payment.currencyRateParameters.type) {
          this.validationPaymentErrors[assetIndex][paymentIndex].push("currencyCodeType");
        }
        if (payment.currencyRateParameters.type === 'Imported' && !payment.currencyRateParameters.importedId) {
         this.validationPaymentErrors[assetIndex][paymentIndex].push("currentImportedId");
        }
      }
    },
    arrayIsNotEmpty(array) {
      return Array.isArray(array) && array.length > 0 && array.every(a => !!a);
    },
    onKeyupEvent() {
      return true;
    }
  }
};
</script>
